import { useCallback, useEffect, useState } from "react";
import type { ApprovalCard, Bootstrap, Capabilities, LedgerEvent, ChatMessage } from "../lib/api";
import { overviewPanels } from "../lib/workspaces";
import { readAgentView, wakesOnDemand, type AgentInstance } from "./api";
import { toolIndex, type ToolIndex } from "../studio/ask";
import { studioWindow } from "../studio/project";
import { ownerConversations, recentChoice, type RecentChoice } from "./RecentChoices";
import { channelLabels } from "../studio/declaredReview";
import type { OverviewPreview } from "./OverviewPanels";
import { recentReplies, type RecentReply } from "./RecentReplies";

export interface AgentReading {
  state: "reading" | "sleeping" | "offline" | "failed" | "ready"; boot?: Bootstrap; pending?: ApprovalCard[];
  approvalsState?: "reading" | "failed" | "ready";
  tools?: ToolIndex; channels?: Record<string, string>;
  choices?: RecentChoice[]; choicesError?: string; choicesState?: "reading" | "failed" | "ready";
  previews?: Record<string, OverviewPreview>; previewState?: "reading" | "ready" | "failed";
  previewFailures?: string[];
  replies?: RecentReply[]; repliesState?: "reading" | "ready" | "failed";
}

/**
 * A read of an Agent's state fails now and then without the Agent being unreadable: a request that dies on
 * the way, an Agent that is busy or starting.  One failure keeps what the page last read and asks again
 * soon, after these waits; only the failure after the last wait is said, and the page then keeps asking at
 * the usual pace, so what it said goes away by itself when the Agent answers (walk W4: 「暂时没有读到完整状态，
 * 请刷新重试」 for five seconds, then fine).
 */
const RETRY_AFTER = [1000, 2000];
const READS_BEFORE_SAYING = RETRY_AFTER.length + 1;
const REFRESH_EVERY = 5000;

export function useHubReadings(agents: AgentInstance[], page: "overview" | "people" | "responses" | null) {
  const [readings, setReadings] = useState<Record<string, AgentReading>>({});
  const [refresh, setRefresh] = useState(0);
  // A refresh is still pending until this pass has been read or failed.
  const [settled, setSettled] = useState("");
  const [visible, setVisible] = useState(() => document.visibilityState !== "hidden");
  useEffect(() => {
    const changed = () => setVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", changed);
    return () => document.removeEventListener("visibilitychange", changed);
  }, []);
  const signature = JSON.stringify(agents.map(agent => [agent.id, agent.online, agent.desired_running, agent.runtime?.state]));
  const pass = `${page}:${refresh}:${visible}:${signature}`;
  useEffect(() => {
    const controller = new AbortController();
    const timers = new Set<number>();
    const later = (ms: number, run: () => void) => {
      const timer = window.setTimeout(() => { timers.delete(timer); run(); }, ms);
      timers.add(timer);
    };
    // Readings belong to the signed-in directory, not to whichever page is
    // currently visible. Keep known content while refreshing it in place.
    setReadings(old => Object.fromEntries(agents.map(agent => {
      const previous = old[agent.id];
      if (!agent.online) return [agent.id, { state: wakesOnDemand(agent) ? "sleeping" : "offline" }];
      return [agent.id, {
        ...previous, state: previous?.boot ? "ready" : "reading",
        approvalsState: page === "responses" || !previous?.pending ? "reading" : previous.approvalsState,
        previewState: page === "overview" ? "reading" : previous?.previewState,
        repliesState: page === "overview" ? "reading" : previous?.repliesState,
        choicesState: page === "responses" ? "reading" : previous?.choicesState,
      }];
    })));
    // Workspace and settings pages have no use for these full Agent records.
    // Their visible frame supplies its own live summary instead. Hidden pages
    // also release in-flight observations, not only the next polling timer.
    if (!page || !visible) {
      setSettled(pass);
      return () => controller.abort();
    }
    // What each online Agent still owes this pass before the page is current.
    const owed = new Map<string, Set<string>>(agents.filter(agent => agent.online).map(agent =>
      [agent.id, new Set(["boot", "approvals", ...(page === "responses" ? ["choices"] : [])])]));
    const arrive = (id: string, part: string) => {
      if (controller.signal.aborted) return;
      owed.get(id)?.delete(part);
      if ([...owed.values()].every(parts => !parts.size)) setSettled(pass);
    };
    if (!owed.size) setSettled(pass);
    for (const agent of agents) {
      if (!agent.online) continue;
      const update = (patch: Partial<AgentReading> | ((previous: AgentReading | undefined) => Partial<AgentReading>)) => {
        if (!controller.signal.aborted) setReadings(old => ({ ...old,
          [agent.id]: { ...old[agent.id], ...(typeof patch === "function" ? patch(old[agent.id]) : patch) } }));
      };
      // The overview's reads of the Agent's panels fail now and then as the state read does (walk W5: 「工作记录暂未读完整」
      // for one poll while the Agent was busy).  What the earlier reads gave stays through a failed one, the read is
      // tried again after the same waits, and only the failure after the last wait is said.  The usual refresh goes on
      // asking, so what was said goes away by itself when the Agent answers.
      let previewFailed = 0;
      let previewReading = false;
      let previewRetry = 0;
      let latestBoot: Bootstrap | undefined;
      const readOverview = async (boot: Bootstrap) => {
        if (page !== "overview") return;
        latestBoot = boot;
        if (!boot.owner?.present) { update({ previews: {}, previewState: "failed" }); return; }
        if (previewReading || controller.signal.aborted) return;
        previewReading = true;
        window.clearTimeout(previewRetry);
        const panels = overviewPanels(boot.panels ?? []).filter(panel => panel.audience === "owner" && panel.overview_preview);
        const request = new AbortController();
        const abort = () => request.abort();
        controller.signal.addEventListener("abort", abort, { once: true });
        const timeout = window.setTimeout(abort, 10000);
        try {
          const results = await Promise.allSettled(panels.map(async panel => {
            const result = await readAgentView<OverviewPreview>(agent.id, `/api/panels/${encodeURIComponent(panel.key)}/overview`, request.signal, true);
            if (result.generation_id !== panel.generation_id || !Array.isArray(result.items)) throw new Error("stale preview");
            return [panel.key, result] as const;
          }));
          const read = Object.fromEntries(results.flatMap(result => result.status === "fulfilled" ? [result.value] : []));
          const failures = results.flatMap((result, index) => result.status === "rejected" ? [panels[index].key] : []);
          if (!failures.length) previewFailed = 0;
          else previewFailed += 1;
          if (failures.length && previewFailed < READS_BEFORE_SAYING) {
            // Not said yet: the panels that did answer are current, the ones that did not keep what they last gave.
            // What is held is drawn now; a page that holds nothing yet goes on saying it is still reading.
            update(previous => {
              const held = { ...Object.fromEntries(failures.flatMap(key => previous?.previews?.[key] ? [[key, previous.previews[key]]] : [])), ...read };
              return { previews: held, previewState: Object.keys(held).length ? "ready" : "reading" };
            });
            previewRetry = window.setTimeout(() => { timers.delete(previewRetry); if (latestBoot) void readOverview(latestBoot); }, RETRY_AFTER[previewFailed - 1]);
            timers.add(previewRetry);
          } else {
            update({ previews: read, previewFailures: failures, previewState: failures.length ? "failed" : "ready" });
          }
        } finally { window.clearTimeout(timeout); controller.signal.removeEventListener("abort", abort); previewReading = false; }
      };
      let capabilitiesReading = false;
      const readCapabilities = () => {
        if (!page || capabilitiesReading || controller.signal.aborted) return;
        capabilitiesReading = true;
        void readAgentView<Capabilities>(agent.id, "/api/capabilities", controller.signal, true).then(capabilities => {
          update({ tools: capabilities.registries ? toolIndex(capabilities.registries) : {}, channels: channelLabels(capabilities.registries) });
        }).catch(() => update({ channels: {} })).finally(() => { capabilitiesReading = false; });
      };
      let approvalsReading = false;
      let approvalsFailed = 0;
      const readApprovals = (first = false) => {
        if (approvalsReading || controller.signal.aborted) return;
        approvalsReading = true;
        void readAgentView<{ pending: ApprovalCard[] }>(agent.id, "/api/approvals", controller.signal, true).then(approvals => {
          if (!Array.isArray(approvals.pending)) throw new Error("待决项没有读完整");
          approvalsFailed = 0;
          update({ pending: approvals.pending, approvalsState: "ready" });
        }).catch(() => {
          approvalsFailed += 1;
          // The list already read stays while the next reads are tried; the page says it could not read after
          // the last try, and the usual refresh goes on asking.
          if (approvalsFailed >= READS_BEFORE_SAYING) update({ approvalsState: "failed" });
          else later(RETRY_AFTER[approvalsFailed - 1], () => readApprovals(first));
        }).finally(() => {
          approvalsReading = false;
          if (first && (!approvalsFailed || approvalsFailed >= READS_BEFORE_SAYING)) arrive(agent.id, "approvals");
        });
      };
      readApprovals(true);
      readCapabilities();
      const readChoices = async (boot: Bootstrap) => {
        if (page !== "responses") return;
        update({ choicesState: "reading", choicesError: undefined });
        try {
          const query = studioWindow(300);
          query.set("kind", "l0.inbound");
          const inputs = await readAgentView<{ events: LedgerEvent[] }>(agent.id, `/api/timeline?${query}`, controller.signal, true);
          if (!Array.isArray(inputs.events)) throw new Error("对话来源尚未读到");
          const sessions = ownerConversations(boot.sessions, inputs.events);
          const conversations = await Promise.allSettled(sessions.map(async session => ({ session: session.id,
            ...(await readAgentView<{ messages: ChatMessage[] }>(agent.id, `/api/chat/${encodeURIComponent(session.id)}`, controller.signal, true)) })));
          const foundChoices: RecentChoice[] = [];
          for (const result of conversations) {
            if (result.status !== "fulfilled" || !Array.isArray(result.value.messages)) {
              throw new Error("部分最近工作台对话尚未读到");
            }
            const { session, messages } = result.value;
            const choice = recentChoice(messages, session);
            if (choice) foundChoices.push(choice);
          }
          update({ choices: foundChoices, choicesState: "ready",
            choicesError: inputs.events.length >= 300 ? "这里只读取了有限记录中的最近工作台对话；较早的选择请回到 Agent 查看。" : undefined });
        } catch { update({ choicesState: "failed", choicesError: "最近工作台对话暂未读到，请打开原 Agent 核对。" }); }
        arrive(agent.id, "choices");
      };
      let repliesFailed = 0;
      let repliesReading = false;
      const readReplies = async (boot: Bootstrap) => {
        if (page !== "overview" || repliesReading || controller.signal.aborted) return;
        if (!boot.owner?.present || !boot.owner.principal_id) {
          update({ replies: [], repliesState: "ready" });
          return;
        }
        repliesReading = true;
        const request = new AbortController();
        const abort = () => request.abort();
        controller.signal.addEventListener("abort", abort, { once: true });
        const timeout = window.setTimeout(abort, 10000);
        try {
          const reply = await readAgentView<{ events: LedgerEvent[] }>(agent.id,
            "/api/ledger/obligations?work_only=1&limit=12", request.signal, true);
          if (!Array.isArray(reply.events)) throw new Error("最近回复尚未读到");
          if (latestBoot?.owner?.principal_id !== boot.owner.principal_id) return;
          repliesFailed = 0;
          update({ replies: recentReplies(reply.events, boot.owner.principal_id), repliesState: "ready" });
        } catch {
          repliesFailed += 1;
          if (repliesFailed >= READS_BEFORE_SAYING) update({ repliesState: "failed" });
          else later(RETRY_AFTER[repliesFailed - 1], () => { void readReplies(boot); });
        } finally { window.clearTimeout(timeout); controller.signal.removeEventListener("abort", abort); repliesReading = false; }
      };
      // The state read, once now and then every few seconds. `opened`: it has come back once this pass.
      let failed = 0;
      let opened = false;
      const readState = async () => {
        if (controller.signal.aborted) return;
        // Registry snapshots have no tool receipts. Refresh these declarations independently of
        // conversations so removals cannot retain old faces.
        if (opened) {
          readCapabilities();
          // A pending list is live work too: new requests can arrive, and another page can answer or
          // withdraw one while this Hub stays open.
          readApprovals();
        }
        try {
          const boot = await readAgentView<Bootstrap>(agent.id, "/api/bootstrap", controller.signal, true);
          if (!Array.isArray(boot.principals) || !Array.isArray(boot.sessions) || (opened && !Array.isArray(boot.panels)))
            throw new Error(opened ? "界面清单没有读完整" : "概况没有读完整");
          if (controller.signal.aborted) return;
          failed = 0;
          update({ state: "ready", boot });
          if (!opened) {
            opened = true;
            arrive(agent.id, "boot");
            void readChoices(boot);
          }
          await Promise.all([readOverview(boot), readReplies(boot)]);
        } catch {
          if (controller.signal.aborted) return;
          failed += 1;
          if (failed === READS_BEFORE_SAYING) {
            // `previewState` too: the previews are read from what the bootstrap answers, so a bootstrap that
            // failed is not a preview still being read.  Leaving it at "reading" left the overview saying
            // 「正在读取工作记录…」 for as long as the page stayed open, which is a third sentence about an
            // Agent whose read had already failed and said so on its own card.
            update({ state: "failed", boot: undefined, previewState: "failed", repliesState: "failed",
              ...(opened ? {} : { choicesState: "failed", choicesError: "最近工作台对话暂未读到，请打开原 Agent 核对。" }) });
            if (!opened) { arrive(agent.id, "boot"); arrive(agent.id, "choices"); }
          }
        }
        later(failed ? RETRY_AFTER[failed - 1] ?? REFRESH_EVERY : REFRESH_EVERY, () => void readState());
      };
      void readState();
    }
    return () => { controller.abort(); timers.forEach(timer => window.clearTimeout(timer)); };
  }, [page, signature, refresh, visible]);
  const onRefresh = useCallback(() => setRefresh(value => value + 1), []);
  return { readings, onRefresh, refreshing: Boolean(page) && visible && settled !== pass };
}
